import { useState } from "react";
import { Navigate, useSearchParams, useNavigate } from "react-router-dom";

import type { AuthenticationRequestData } from "../../schemas";
import { LoginTokenLink } from "../../components";
import { useAuthentication, useIdentityClient } from "../../hooks";
import { appendReturnTo } from "../../utils";

import { LoginTemplate } from "./login.template";

const MIN_SSO_SUBMISSION_TIME = 1000 * 5;

export const Login = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const { applicationName, ssoOnly } = useAuthentication();

	const [ssoError, setSsoError] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const { client } = useIdentityClient();

	const returnTo = searchParams.get("returnTo");

	const doSsoRedirect = async (redirectUrl: string) => {
		window.location.replace(appendReturnTo(redirectUrl, returnTo));

		await new Promise((res) => setTimeout(res, MIN_SSO_SUBMISSION_TIME));
	};

	const handleSsoOnlySignIn = async (data: AuthenticationRequestData): Promise<void> => {
		const result = await client.GetUserSsoInfo(data.username, applicationName, returnTo);

		if (!result.success || !result.data || !result.data.redirectUrl) {
			setSsoError(true);
			return;
		}

		await doSsoRedirect(result.data.redirectUrl);
	};

	const handleMixedSignIn = async (data: AuthenticationRequestData) => {
		const result = await client.GetUserSsoInfo(data.username, applicationName, returnTo);

		if (!result.success || !result.data || !result.data.isSsoEnabled || !result.data.redirectUrl) {
			navigate(appendReturnTo(`/login-password?username=${encodeURIComponent(data.username)}`, returnTo));
			return;
		}

		if (result.data.disablePasswordLogin === false) {
			navigate(
				appendReturnTo(
					`/login-interstitial?username=${encodeURIComponent(data.username)}&redirect=${encodeURIComponent(result.data.redirectUrl)}`,
					returnTo,
				),
			);
			return;
		}

		await doSsoRedirect(result.data.redirectUrl);
	};

	const handleSSOSignIn = async (data: AuthenticationRequestData): Promise<void> => {
		if (ssoOnly) {
			return handleSsoOnlySignIn(data);
		}

		return handleMixedSignIn(data);
	};

	const handleSubmit = async (data: AuthenticationRequestData) => {
		if (submitting) {
			return;
		}

		setSubmitting(true);
		setSsoError(false);

		try {
			await handleSSOSignIn(data);
		} finally {
			setSubmitting(false);
		}
	};

	const token = searchParams.get("token");

	if (token) {
		return (
			<Navigate
				to={{
					pathname: "/login-sso-verify",
					search: searchParams.toString(),
				}}
				replace
			/>
		);
	}

	return (
		<>
			<LoginTemplate
				onSubmit={handleSubmit}
				isSubmitting={submitting}
				isSsoError={ssoError}
				defaultUsername={searchParams.get("username") || ""}
			/>
			<LoginTokenLink />
		</>
	);
};
